/**
 * ID lookup – find AI-team work by a short ID (`GET /api/v1/admin/lookup`).
 * Types mirror `system/services/id_lookup.py`. Concurrent lookups are batched
 * into one request, so a page full of agent text costs one round trip.
 * Admin-only, like the Team area the targets live in.
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { useIsAdmin } from "../hooks/useAuth";
import { LOOKUP_MAX, findIdTokens, tokenHref } from "../lib/idLinks";
import type { ReviewKind } from "./admin";
import { api } from "./client";

export type IdKind = "question" | "job" | "review" | "wave" | "goal" | "task";

export interface IdMatch {
  kind: IdKind;
  id: string;
  title: string;
  status: string | null;
  created_at: string | null;
  /** question: the asking employee; review: the author; goal: the assigned employee. */
  agent_id?: string | null;
  scope_type?: string;
  scope_id?: string;
  review_kind?: ReviewKind;
  version?: number;
}

interface LookupResponse {
  results: Record<string, IdMatch[]>;
  invalid: string[];
}

interface Waiter {
  ids: string[];
  resolve: (r: Record<string, IdMatch[]>) => void;
  reject: (e: unknown) => void;
}

let waiting: Waiter[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;

async function flush() {
  const batch = waiting;
  waiting = [];
  timer = null;
  const all = [...new Set(batch.flatMap((w) => w.ids))];
  try {
    const results: Record<string, IdMatch[]> = {};
    for (let i = 0; i < all.length; i += LOOKUP_MAX) {
      const qs = new URLSearchParams();
      for (const id of all.slice(i, i + LOOKUP_MAX)) qs.append("q", id);
      Object.assign(results, (await api.get<LookupResponse>(`/api/v1/admin/lookup?${qs}`)).results);
    }
    for (const w of batch) w.resolve(Object.fromEntries(w.ids.map((id) => [id, results[id.toLowerCase()] ?? []])));
  } catch (e) {
    for (const w of batch) w.reject(e);
  }
}

/** Resolve IDs; calls within the same few milliseconds share one request. */
export function resolveIds(ids: string[]): Promise<Record<string, IdMatch[]>> {
  return new Promise((resolve, reject) => {
    waiting.push({ ids, resolve, reject });
    if (timer === null) timer = setTimeout(flush, 15);
  });
}

/** The matches for a set of IDs; off for non-admins and empty input. */
export function useIdLookup(ids: string[]) {
  const isAdmin = useIsAdmin();
  const key = useMemo(() => [...new Set(ids)].sort(), [ids]);
  return useQuery({
    queryKey: ["id-lookup", ...key],
    queryFn: () => resolveIds(key),
    enabled: isAdmin === true && key.length > 0,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

/** `token → href` for the IDs in a text that resolve to something. */
export function useIdLinks(text: string | null | undefined): ReadonlyMap<string, string> {
  const tokens = useMemo(() => findIdTokens(text), [text]);
  const lookup = useIdLookup(tokens);
  return useMemo(() => {
    const links = new Map<string, string>();
    for (const [token, matches] of Object.entries(lookup.data ?? {})) {
      const href = tokenHref(token, matches);
      if (href) links.set(token, href);
    }
    return links;
  }, [lookup.data]);
}
